<template>
  <component
    v-bind="$attrs"
    :is="inputType"
    :id="$attrs.name"
    :error="field.$error"
    :error-messages="errorMessage"
    :model-value="field.$model"
    :required="!!field?.required || $attrs.required"
    :return-object="returnObject"
    :type="type"
    @on-change="handleModelUpdate"
    @update:modelValue="handleModelUpdate($event)"
  >
    <template v-for="(_, name) of $slots"
              :key="slot"
              #[name]="slotProps">
      <slot :name
            v-bind="slotProps" />
    </template>
    <slot name="default" />
    <template v-if="errorMessage"
              #errors>
      <p class="error-msg">
        {{ $attrs.errorMessage || errorMessage }}
      </p>
    </template>
  </component>
</template>

<script>
  import BvDateInput from '@bv/components/form/inputs/date/BvDateInput.vue';
  import BvSelectInput from '@bv/components/form/inputs/select/BvSelectInput.vue';

  import BevaRangeSlider from '@/components/SharedComponents/BevaRangeSlider/BevaRangeSlider.vue';
  import BevaSelect from '@/components/SharedComponents/BevaSelect/BevaSelect.vue';
  import BevaUploadCSV from '@/components/SharedComponents/BevaUpload/BevaUpload.vue';
  import BevaApiSelector from '@/components/SharedComponents/Inputs/BevaApiSelector.vue';
  import BevaRadio from '@/components/SharedComponents/Inputs/BevaRadio.vue';
  import BevaInputColor from '@/components/SharedComponents/Inputs/InputColor.vue';
  import SwitcherHandler from '@/components/SharedComponents/Inputs/SwitcherHandler.vue';
  import FormInput from '@/components/SharedComponents/Settings/Inputs/TextInput.vue';
  import { accessDeep } from '@/utilities';




  export default {
    name: 'DynamicField',
    components: {
      FormInput,
      BevaSelect,
      BevaApiSelector,
      BevaRadio,
      SwitcherHandler,
      BevaUploadCSV,
      BevaInputColor,
      BvDateInput,
      BevaRangeSlider,
      BvSelectInput,
    },
    inject: ['form', 'updateForm'],
    props: {
      type: {
        type: String,
        required: true,
      },
      returnObject: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      inputType() {
        switch (this.type) {
          case 'text':
            return 'FormInput';
          case 'select':
            return 'BevaSelect';
          case 'api-select':
            return 'BevaApiSelector';
          case 'radio':
            return 'BevaRadio';
          case 'switch':
            return 'SwitcherHandler';
          case 'upload':
            return 'BevaUploadCSV';
          case 'color':
            return 'BevaInputColor';
          case 'date':
            return 'BvDateInput';
          case 'range':
            return 'BvDateInput';
          case 'range-slider':
            return 'BevaRangeSlider';
          case 'new-select':
            return 'BvSelectInput';
          default:
            return 'FormInput';
        }
      },
      field() {
        return accessDeep(this.form.v$.form, this.$attrs.name);
      },
      errorMessage() {
        let errMsg = this.field?.$errors[0]?.$message;
        if (!errMsg) {
          return '';
        }

        if (Array.isArray(errMsg)) {
          for (const error of errMsg) {
            if (error && error[0]) {
              errMsg = error[0];

              break;
            }
          }
        }

        return errMsg.replace(
          this.$attrs.name,
          this.$attrs.propertyName ||
            this.$attrs['property-name'] ||
            this.$attrs.labelText ||
            this.$attrs['label-text'] ||
            this.$attrs.label,
        );
      },
    },
    methods: {
      handleModelUpdate(value, isEditMode = false) {
        const { trackBy, name: field } = this.$attrs;

        this.updateForm({
          field,
          value,
          trackBy: trackBy || this.$attrs['track-by'],
          isEditMode,
          returnObject: this.returnObject,
        });
        // exposing the value to the parent component if needed for disabling another input and etc
        this.$emit('get:modelValue', {
          field,
          value,
        });
      },
    },
    created() {
      // Update submit data object with edit value in case of editing form
      this.$nextTick(() => {
        const field = accessDeep(this.form.form, this.$attrs.name);
        field && this.handleModelUpdate(field, true);
      });
    },
  };
</script>

<style>
.error-msg {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  color: var(--error-500, #ff3b30);
}
</style>
